import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { User, Client } from '../../types';
import $ from 'jquery';
import 'select2';
import 'select2/dist/css/select2.min.css';
import './UserForm.css';

interface UserFormProps {
  clients: Client[];
  onSuccess?: () => void;
  clientsLoading?: boolean;
  editUser?: User | null;
  onCancel?: () => void;
  /** When false, only "Company" role is allowed (e.g. for Admin User in Company Dashboard) */
  allowAdminRole?: boolean;
}

type RoleOption = 'admin' | 'client' | 'user_admin';
const UserForm: React.FC<UserFormProps> = ({ clients, onSuccess, clientsLoading = false, editUser = null, onCancel, allowAdminRole = true }) => {
  const isEditMode = !!editUser;
  const [firstName, setFirstName] = useState(editUser?.first_name || '');
  const [lastName, setLastName] = useState(editUser?.last_name || '');
  const [email, setEmail] = useState(editUser?.email || '');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [role, setRole] = useState<RoleOption>((editUser?.role as RoleOption) || 'client');
  const [clientIds, setClientIds] = useState<number[]>(editUser?.clients?.map(c => c.id) || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const select2Ref = useRef<HTMLSelectElement>(null);
  const select2InstanceRef = useRef<any>(null);

  // Update form when editUser changes
  useEffect(() => {
    if (editUser) {
      setFirstName(editUser.first_name || '');
      setLastName(editUser.last_name || '');
      setEmail(editUser.email || '');
      setPassword('');
      setPasswordConfirm('');
      setRole((editUser.role as RoleOption) || 'client');
      setClientIds(editUser.clients?.map(c => c.id) || []);
    }
  }, [editUser]);

  const PASSWORD_REQUIREMENTS = '8+ characters, upper & lower case, number, and symbol';

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[a-z]/.test(pwd)) return 'Password must include a lowercase letter';
    if (!/[A-Z]/.test(pwd)) return 'Password must include an uppercase letter';
    if (!/\d/.test(pwd)) return 'Password must include a number';
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) return 'Password must include a symbol';
    return null;
  };

  useEffect(() => {
    if (role === 'admin') {
      setClientIds([]);
      if (select2InstanceRef.current && select2Ref.current) {
        $(select2Ref.current).select2('destroy');
        select2InstanceRef.current = null;
      }
    }
  }, [role]);

  // When Admin Role not allowed, force client role for new users
  const effectiveRole = !allowAdminRole && !isEditMode ? 'client' : role;

  // Initialize Select2 when clients are loaded and role is client or user_admin
  useEffect(() => {
    if ((role === 'client' || role === 'user_admin') && !clientsLoading && clients.length > 0 && select2Ref.current) {
      // Destroy existing Select2 instance if it exists
      if (select2InstanceRef.current && select2Ref.current) {
        $(select2Ref.current).select2('destroy');
        select2InstanceRef.current = null;
      }

      // Initialize Select2
      const $select = $(select2Ref.current);
      $select.select2({
        placeholder: 'Select companies...',
        allowClear: true,
        width: '100%',
        multiple: true,
      });

      // Store the Select2 instance
      select2InstanceRef.current = $select.data('select2');

      // Handle Select2 change event
      $select.on('change', (e: any) => {
        const selectedValues = $select.val() as string[] | null;
        const selectedIds = selectedValues 
          ? selectedValues.map(val => Number(val))
          : [];
        setClientIds(selectedIds);
      });

      // Set initial values if any (for edit mode)
      if (clientIds.length > 0) {
        $select.val(clientIds.map(String)).trigger('change');
      }

      // Cleanup function
      return () => {
        $select.off('change');
        if (select2Ref.current) {
          try {
            $(select2Ref.current).select2('destroy');
          } catch (e) {
            // Select2 might already be destroyed
          }
          select2InstanceRef.current = null;
        }
      };
    } else if (role === 'admin' && select2Ref.current) {
      // Clean up Select2 when role changes to admin
      if (select2InstanceRef.current) {
        try {
          $(select2Ref.current).select2('destroy');
        } catch (e) {
          // Select2 might already be destroyed
        }
        select2InstanceRef.current = null;
      }
    }
  }, [role, clients, clientsLoading]);

  // Update Select2 when clientIds change externally
  useEffect(() => {
    if ((role === 'client' || role === 'user_admin') && select2Ref.current && select2InstanceRef.current) {
      const $select = $(select2Ref.current);
      const currentValues = $select.val() as string[] | null;
      const expectedValues = clientIds.map(String);
      
      if (JSON.stringify(currentValues?.sort()) !== JSON.stringify(expectedValues.sort())) {
        $select.val(expectedValues).trigger('change');
      }
    }
  }, [clientIds, role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    const submitRole = !allowAdminRole && !isEditMode ? 'client' : role;
    try {
      const payload: any = {
        first_name: firstName,
        last_name: lastName,
        role: submitRole,
      };

      // Password validation
      if (isEditMode) {
        if (password) {
          const pwdErr = validatePassword(password);
          if (pwdErr) {
            setError(pwdErr);
            setLoading(false);
            return;
          }
          if (password !== passwordConfirm) {
            setError('Passwords do not match');
            setLoading(false);
            return;
          }
          payload.password = password;
          payload.password_confirmation = passwordConfirm;
        }
      } else {
        if (!password) {
          setError('Password is required');
          setLoading(false);
          return;
        }
        const pwdErr = validatePassword(password);
        if (pwdErr) {
          setError(pwdErr);
          setLoading(false);
          return;
        }
        if (password !== passwordConfirm) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        payload.password = password;
        payload.password_confirmation = passwordConfirm;
        payload.email = email;
      }

      if (submitRole === 'client' || submitRole === 'user_admin') {
        if (clientIds.length === 0) {
          setError('At least one company is required for company users');
          setLoading(false);
          return;
        }
        payload.client_ids = clientIds;
      }

      if (isEditMode && editUser) {
        // Update existing user
        await api.put<User>(`/users/${editUser.id}`, payload);
        setSuccess('User updated successfully!');
      } else {
        // Create new user
        await api.post<User>('/users', payload);
        setSuccess('User created successfully!');
        setFirstName('');
        setLastName('');
        setEmail('');
        setPassword('');
        setPasswordConfirm('');
        setRole('client');
        setClientIds([]);
        
        // Reset Select2
        if (select2Ref.current) {
          $(select2Ref.current).val([]).trigger('change');
        }
      }
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} user. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-form-container">
      <h3>{isEditMode ? 'Edit User' : 'Create New User'}</h3>
      <form onSubmit={handleSubmit} className="user-form">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="form-group">
          <label htmlFor="first_name">First Name</label>
          <input
            type="text"
            id="first_name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            placeholder="Enter first name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="last_name">Last Name</label>
          <input
            type="text"
            id="last_name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            placeholder="Enter last name"
          />
        </div>

        {!isEditMode && (
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter user email"
            />
          </div>
        )}

        {isEditMode && (
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              disabled
              style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
              placeholder="Email cannot be changed"
            />
            <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              Email cannot be edited
            </small>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required={!isEditMode}
            placeholder={isEditMode ? "Enter new password (leave empty to keep current)" : "Enter password"}
          />
          <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
            {PASSWORD_REQUIREMENTS}
            {isEditMode && ' Leave empty to keep current password.'}
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="password_confirm">Confirm Password</label>
          <input
            type="password"
            id="password_confirm"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required={!isEditMode || !!password}
            placeholder={isEditMode && !password ? "Leave empty to keep current" : "Re-enter password"}
          />
        </div>

        {allowAdminRole && (
          <div className="form-group">
            <label htmlFor="role">Role</label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as RoleOption)}
              required
            >
              <option value="admin">Admin</option>
              <option value="client">Company</option>
              <option value="user_admin">Admin User</option>
            </select>
          </div>
        )}
        {!allowAdminRole && isEditMode && (
          <div className="form-group">
            <label htmlFor="role">Role</label>
            <select
              id="role"
              value={role === 'admin' ? 'client' : role}
              onChange={(e) => setRole(e.target.value as RoleOption)}
              required
            >
              <option value="client">Company</option>
              <option value="user_admin">Admin User</option>
            </select>
          </div>
        )}

        {(effectiveRole === 'client' || effectiveRole === 'user_admin') && (
          <div className="form-group">
            <label htmlFor="client_ids">Companies (Select multiple)</label>
            {clientsLoading ? (
              <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px', color: '#666' }}>
                Loading companies...
              </div>
            ) : clients.length === 0 ? (
              <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px', color: '#666' }}>
                No companies available. Please create companies first.
              </div>
            ) : (
              <select
                id="client_ids"
                ref={select2Ref}
                multiple
                required
                style={{ width: '100%' }}
              >
                {clients.map((client) => (  
                  <option key={client.id} value={client.id}>
                    {client.client_name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          {isEditMode && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
          )}
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update User' : 'Create User')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
